<template>
	<v-stepper
		v-model:model-value="checkoutStepper"
		non-linear
	>
		<v-stepper-header>
			<v-stepper-item
				:complete="checkoutStepper > 1"
				:value="1"
				editable
				color="primary"
			>
				<translate>Details</translate>
			</v-stepper-item>
			<v-divider />
			<v-stepper-item
				:complete="checkoutStepper > 2"
				:value="2"
				:editable="informationComplete"
				:color="checkoutStepper > 1 ? 'primary' : ''"
			>
				<translate>Device details</translate>
			</v-stepper-item>
			<v-divider />
			<v-stepper-item
				:complete="checkoutStepper > 3"
				:value="3"
				:editable="formComplete"
				:color="checkoutStepper > 2 ? 'primary' : ''"
			>
				<translate>Overview</translate>
			</v-stepper-item>
		</v-stepper-header>
		<v-stepper-window>
			<v-stepper-window-item
				:value="1"
			>
				<v-container>
					<v-row>
						<v-col>
							<checkout-information
								ref="checkoutInfoForm"
								v-model:model-value="formData.information"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col
							class="text-right"
						>
							<v-btn
								v-if="!informationIncomplete"
								class="readonly-button"
								variant="text"
								color="primary"
								readonly
								:ripple="false"
							>
								<v-icon
									:start="$vuetify.display.smAndUp"
									color="primary"
									icon="$lock"
								/>
								<span
									v-if="$vuetify.display.smAndUp"
								>
									{{ $gettext('Your data is safe') }}
								</span>
							</v-btn>
							<v-btn
								v-if="informationIncomplete && $vuetify.display.smAndUp"
								class="readonly-button"
								variant="text"
								color="error"
								readonly
								:ripple="false"
							>
								{{ $gettext('Form Incomplete') }}
							</v-btn>
							<v-btn
								color="primary"
								class="ml-2"
								@click="nextIfInformationComplete"
							>
								<translate>
									Next
								</translate>
							</v-btn>
						</v-col>
					</v-row>
				</v-container>
			</v-stepper-window-item>
			<v-stepper-window-item
				:value="2"
			>
				<v-container>
					<v-row>
						<v-col>
							<checkout-device
								ref="checkoutDeviceForm"
								v-model:model-value="formData.device"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col
							:class="'flex-grow-1 flex-shrink-0 text-right'"
						>
							<v-btn
								v-if="!showIncomplete"
								class="readonly-button"
								variant="text"
								color="primary"
								readonly
								:ripple="false"
							>
								<v-icon
									color="primary"
									:start="$vuetify.display.smAndUp"
									icon="$lock"
								/>
								<span
									v-if="$vuetify.display.smAndUp"
								>
									{{ $gettext('Your data is safe') }}
								</span>
							</v-btn>
							<v-btn
								v-if="showIncomplete && $vuetify.display.smAndUp"
								class="readonly-button"
								variant="text"
								color="error"
								readonly
								:ripple="false"
							>
								{{ $gettext('Form Incomplete') }}
							</v-btn>
							<v-btn
								color="primary"
								class="ml-2"
								@click="nextIfComplete"
							>
								<translate>
									Next
								</translate>
							</v-btn>
						</v-col>
					</v-row>
				</v-container>
			</v-stepper-window-item>
			<v-stepper-window-item
				:value="3"
			>
				<v-container>
					<v-row>
						<v-col>
							<checkout-summary
								v-model:model-value="formData.authorization"
								:data="formData"
								:complete="complete"
								:price="price"
								:device-name="deviceName"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col
							:class="'flex-grow-1 flex-shrink-0 text-right'"
						>
							<v-btn
								class="readonly-button"
								variant="text"
								color="primary"
								readonly
								:ripple="false"
							>
								<v-icon
									:start="$vuetify.display.smAndUp"
									color="primary"
									icon="$lock"
								/>
								<span
									v-if="$vuetify.display.smAndUp"
								>
									{{ $gettext('Your data is safe') }}
								</span>
							</v-btn>
							<v-btn
								color="primary"
								:disabled="!formData.authorization || loading"
								:loading="loading"
								@click="signUp"
							>
								<translate>
									Sign Up
								</translate>
							</v-btn>
						</v-col>
					</v-row>
				</v-container>
			</v-stepper-window-item>
			<v-stepper-window-item
				:value="4"
			>
				<v-container>
					<v-row>
						<v-col
							class="d-flex justify-center align-center"
						>
							<v-icon
								color="primary"
								icon="$checkCircle"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-spacer />
						<v-col
							cols="5"
						>
							<p class="text-center">
								<translate>
									Thank you for your Tulip Assist insurance application!
								</translate>
								<br>
								<translate>
									Tulip Assist will let you know by e-mail within two working days whether your request can be processed or whether they need anything else. Once your application has been accepted, the insurance will take effect immediately and you will receive the policy schedule.
								</translate>
							</p>
						</v-col>
						<v-spacer />
					</v-row>
				</v-container>
			</v-stepper-window-item>
		</v-stepper-window>
	</v-stepper>
</template>

<script>
import CheckoutInformation from '../molecules/CheckoutInformation.vue';
import CheckoutDevice from '../molecules/CheckoutDevice.vue';
import CheckoutSummary from '../molecules/CheckoutSummary.vue';
import {
	axios,
} from '../../services/axios-service';
import {
	displayError,
} from 'laravel-front-library';

export default {
	name: 'CheckoutForm',
	components: {
		CheckoutSummary,
		CheckoutDevice,
		CheckoutInformation
	},

	props: {
		complete: {
			type: Boolean,
			required: true,
		},
		price: {
			type: Number,
			required: true,
		},
		deviceName: {
			type: String,
			required: true,
		},
		deviceId: {
			type: Number,
			required: true,
		},
		productVersionId: {
			type: Number,
			required: true,
		},
	},

	emits: ['step',],

	data () {
		return {
			showIncomplete: false,
			informationIncomplete: false,
			checkoutStepper: 1,
			loading: false,
			formData: {
				information: {
					gender: null,
					firstName: null,
					infix: null,
					lastName: null,
					birthDate: '',
					phoneNumber: null,
					emailAddress: null,
					address: {
						street: null,
						housenumber: null,
						postalcode: null,
						city: null,
					},
				},
				device: {
					name: this.deviceName,
					imei: null,
					ean: null,
					purchaseDate: '',
					purchasePrice: '',
					purchasedAt: null,
					iban: null,
					policyAuthorization: false,
				},
				authorization: false
			},
			optional: [
				'infix',
				'housenumberSuffix',
			],
			addressRequried: [
				'street',
				'housenumber',
				'postalcode',
				'city',
			],
			sendInfo: true
		};
	},

	computed: {
		informationComplete () {
			return !Object.entries(this.formData.information).some(([key, value, ]) => {
				if (!this.optional.includes(key)) {
					if (key === 'address') {
						return Object.entries(value).some(([objectKey, objectValue, ]) => {
							if (this.addressRequried.includes(objectKey)) {
								return !objectValue;
							}
						});
					}
					return !value;
				}
			});
		},

		deviceComplete () {
			return !Object.entries(this.formData.device).some(([key, value, ]) => {
				if (!this.optional.includes(key)) {
					return !value;
				}
			});
		},

		formComplete () {
			return this.informationComplete && this.deviceComplete;
		},
	},

	watch: {
		checkoutStepper (value) {
			this.$emit('step', value);
		},

		informationComplete (value) {
			this.informationIncomplete = value ? false : this.informationIncomplete;
		},

		formComplete (value) {
			this.showIncomplete = value ? false : this.showIncomplete;
		}
	},

	created () {
		window.addEventListener('beforeunload', this.sendInfoToSession);
	},

	mounted () {
		this.retrieveInfoFromSession();
	},

	methods: {
		/**
		 * Validate and submit form data
		 */
		signUp () {
			this.loading = true;
			axios.post('/checkout', {
				client: {
					email: this.formData.information.emailAddress,
					name: {
						first: this.formData.information.firstName,
						infix: this.formData.information.infix,
						last: this.formData.information.lastName
					},
					gender: this.formData.information.gender,
					dateBirth: this.formData.information.birthDate,
					telephonenumber: this.formData.information.phoneNumber,
					address: {
						street: this.formData.information.address.street,
						number: this.formData.information.address.housenumber,
						numberSuffix: this.formData.information.address.housenumberSuffix,
						city: this.formData.information.address.city,
						country: this.formData.information.address.country,
						postalcode: this.formData.information.address.postalcode
					},
					bankaccount: {
						iban: this.formData.device.iban
					}
				},
				purchasedAt: this.formData.device.purchasedAt,
				complete: this.complete,
				order: {
					rows: {
						1: {
							phone: {
								ean: this.formData.device.ean,
								imei: this.formData.device.imei,
								purchaseDate: this.formData.device.purchaseDate,
								purchasePriceCent: this.formData.device.purchasePrice * 100,
								deviceId: this.deviceId,
							},
							productVersionId: this.productVersionId
						}
					}
				}
			}).then(() => {
				this.sendInfo = false;
				window.location.href = '/checkout/success';
			}).finally(() => {
				this.loading = false;
			});
		},

		/**
		 * Only allow to go to the 3rd step (summary) if the form is complete.
		 */
		nextIfInformationComplete () {
			if (this.$refs.checkoutInfoForm.validateInformation() && this.informationComplete) {
				this.checkoutStepper = 2;
			} else {
				this.informationIncomplete = true;
			}
		},

		/**
		 * Only allow to go to the 3rd step (summary) if the form is complete.
		 */
		nextIfComplete () {
			if (this.$refs.checkoutDeviceForm.validateInformation() && this.formComplete) {
				this.checkoutStepper = 3;
			} else {
				this.showIncomplete = true;
			}
		},

		/**
		 * Send the data to the session to save before closing/leaving the page.
		 */
		sendInfoToSession () {
			if (this.sendInfo) {
				axios.post('/checkout/checkoutinfotosession', {
					data: this.formData
				});
			}
		},

		/**
		 * Retrieve the data from the session and set the values in the form.
		 */
		retrieveInfoFromSession () {
			axios.get('/checkout/checkoutinfofromsession')
				.then(({
					data
				}) => {
					if (data && data.information) {
						Object.keys(data.information).forEach((key) => {
							this.formData.information[key] = data.information[key] === '' ? null : data.information[key];
							if (key === 'address' && data.information.address) {
								Object.keys(data.information.address).forEach((addressKey) => {
									this.formData.information.address[addressKey] = data.information.address[addressKey] === '' ? null : data.information.address[addressKey];
								});
							}
						});

					}
					if (data && data.device) {
						Object.keys(data.device).forEach((key) => {
							this.formData.device[key] = data.device[key] === '' ? null : data.device[key];
						});
					}
				})
				.catch((error) => {
					if(error.response && error.response.data) {
						displayError(this.$gettext(error.response.data));
					} else {
						displayError(this.$gettext('Something went wrong'));
						window.captureException(error);
					}
				});
		}
	}
};
</script>

<style scoped lang="less">
.readonly-button::before {
	background-color: transparent !important;
}
</style>
